import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControlLabel, MenuItem } from '@mui/material';
import { useGetUsersQuery } from '~/api/v2/hooks';
import { ClearableSelect, SelectPlaceholder } from '~/components';
import { useFullName } from '~/hooks';

interface DataUploaderControllerProps {
  index: number;
  onRemove: (index: number) => void;
  disabled?: boolean;
}

const DataUploaderController: FC<DataUploaderControllerProps> = ({ index, onRemove, disabled }) => {
  const { data, isFetching } = useGetUsersQuery({});
  const { control, setValue, watch } = useFormContext();
  const fieldValues = watch('data_uploaders');
  const primaryDataSteward = watch('data_owner_name');
  const secondaryDataStewards = watch('secondary_data_owners');
  const { fullName } = useFullName();

  return (
    <FormControlLabel
      label="DATA UPLOADERS"
      control={
        <Controller
          name={`data_uploaders.${index}`}
          control={control}
          rules={{
            required: fieldValues.length > 1 && !fieldValues[index].value,
          }}
          render={({ field }) => (
            <ClearableSelect
              {...field}
              disabled={disabled || isFetching}
              // show when only first uploader is selected
              showClearButton={fieldValues.length === 1 && !index && !disabled}
              onClear={() => {
                onRemove(index);
              }}
              onChange={(event) => {
                field.onChange(event);

                const dataUploaderId = event.target.value;
                const ntid = data?.find(({ id }) => id === dataUploaderId)?.profile?.NTID || '';

                setValue(`data_uploaders_network_ids.${index}`, ntid, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              renderValue={() => {
                if (!field?.value) return <SelectPlaceholder>Select data uploader</SelectPlaceholder>;

                return fullName(field.value);
              }}
            >
              {data?.map(({ id, first_name, last_name }) =>
                ![primaryDataSteward, ...secondaryDataStewards, ...fieldValues]?.includes(id) ? (
                  <MenuItem key={id} value={id}>
                    {first_name} {last_name}
                  </MenuItem>
                ) : null,
              )}
            </ClearableSelect>
          )}
        />
      }
    />
  );
};

export default DataUploaderController;
